import {
    Avatar,
    Button,
    FluentProvider,
    Link,
    Spinner,
    Text,
    ToggleButton,
    makeStyles,
    tokens,
    typographyStyles
} from "@fluentui/react-components";
import { Add20Filled, Checkmark20Filled, Dismiss20Regular, OpenRegular } from "@fluentui/react-icons";
import * as microsoftTeams from "@microsoft/teams-js";
import * as React from "react";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import TimeZoneUtils from "sh-application/utility/TimeZoneUtils";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { IRestorableScheduleTeam } from "sh-models";
import { restartApp, UrlFactory } from "sh-application";
import { InstrumentationService, TeamDataService, UserDataService } from "sh-services";
import { TeamMemberRole, TeamMemberType } from "sh-services/lib/cdl/graphql";
import { useThreadMembersByRolePaginated } from "sh-services/lib/cdl/hooks/useThreadMembersByRolePaginated";
import { UserStore } from "sh-stores";
import StringsStore from "sh-strings/store";

import TeamSetupPickerUtils from "../TeamSetupPickerUtils";
import { TeamSetupPickerStepProps } from "../TeamSetupPickerStepProps";
import { TeamSetupPickerStepState } from "../store/schema/TeamSetupPickerStore";
import {
    gotoNextSetupStep,
    setRestorableSchedules,
    setSelectedMSTeam,
    setTeamSetupPickerSequence
} from "../store/store";

const NEW_TEAM_SELECTION = "__new_team_and_schedule__";
const SCHEDULE_ISNT_LISTED_URL = "https://go.microsoft.com/fwlink/?LinkId=2367139";

type View = "picker" | "provisioning";

const useStyles = makeStyles({
    // The card chrome (background, rounded corners, shadow) lives on `.root`. The wrapping
    // FluentProvider would otherwise paint its own opaque, square-cornered background of the
    // same color behind the card, making the rounded corners read as sharp. Keep it transparent.
    provider: {
        backgroundColor: "transparent",
        borderRadius: tokens.borderRadiusXLarge
    },
    root: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
        padding: tokens.spacingHorizontalXXL,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        borderRadius: tokens.borderRadiusXLarge,
        boxShadow: tokens.shadow64,
        width: "100%",
        height: "460px", // hard coded value of $panel-height: 460px;
        boxSizing: "border-box"
    },
    header: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS,
        width: "100%",
        flexShrink: 0
    },
    title: {
        flex: "1 1 auto",
        minWidth: 0,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.subtitle1
    },
    description: {
        margin: 0,
        color: tokens.colorNeutralForeground1,
        flexShrink: 0,
        ...typographyStyles.body1
    },
    listSection: {
        display: "flex",
        flex: "1 1 0",
        flexDirection: "column",
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXXL,
        width: "100%",
        minHeight: 0,
        overflowY: "auto"
    },
    list: {
        display: "flex",
        flexDirection: "column",
        listStyle: "none",
        margin: 0,
        padding: 0,
        width: "100%"
    },
    // Layout only. The hover/pressed/selected backgrounds + their high-contrast-correct text colors
    // come from Fluent's ToggleButton (appearance="subtle"), so no color overrides live here — that
    // is what keeps the team name readable on the Highlight hover background in high contrast.
    item: {
        justifyContent: "flex-start",
        columnGap: tokens.spacingHorizontalM,
        width: "100%",
        maxWidth: "none",
        height: "64px",
        paddingTop: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalM,
        paddingLeft: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalXL,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: "left"
    },
    addIconWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: "40px",
        height: "40px",
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1
    },
    textContainer: {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minWidth: 0
    },
    // Text/icon inherit the ToggleButton's foreground so they follow its high-contrast-correct color
    // in every state (no per-state color overrides needed).
    checkIcon: {
        display: "flex",
        flexShrink: 0,
        marginLeft: tokens.spacingHorizontalM,
        fontSize: "20px"
    },
    itemTitle: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...typographyStyles.body1Strong
    },
    itemSubtitle: {
        display: "flex",
        alignItems: "center",
        color: tokens.colorNeutralForeground3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...typographyStyles.caption1
    },
    subtitleDivider: {
        flexShrink: 0,
        color: tokens.colorNeutralForeground4
    },
    statusRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground2,
        ...typographyStyles.caption1
    },
    errorRow: {
        color: tokens.colorPaletteRedForeground1,
        paddingTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
        ...typographyStyles.caption1
    },
    centerStatus: {
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        rowGap: tokens.spacingVerticalXS,
        paddingTop: tokens.spacingVerticalXXL,
        paddingBottom: tokens.spacingVerticalXXL,
        minHeight: "200px",
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.body1
    },
    centerStatusSubstring: {
        color: tokens.colorNeutralForeground3,
        textAlign: "center"
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        columnGap: tokens.spacingHorizontalM,
        width: "100%",
        flexShrink: 0
    },
    footerLink: {
        display: "inline-flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalXXS
    },
    footerActions: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS,
        flexShrink: 0
    }
});

/**
 * Renders the "Team owner" subtitle under a team in the picker, appending the team's
 * member count once it loads. Counts are fetched per team from CDL via
 * {@link useThreadMembersByRolePaginated}, targeting the team's general-channel thread
 * (TeamInformation.teamId). Rendered as its own component so the hook is called once per
 * team (rules of hooks) and each fetch is independent.
 * @param props.threadId The team's thread id to count members for.
 * @param props.subtitleClassName Class applied to the subtitle text.
 * @returns The subtitle span.
 */
const TeamMemberSubtitle: React.FC<{ threadId: string; subtitleClassName: string }> = ({ threadId, subtitleClassName }) => {
    const styles = useStyles();
    const strings = StringsStore().registeredStringModules.get("teamSetupPicker")!.strings;
    const { data } = useThreadMembersByRolePaginated(
        !!threadId,
        [TeamMemberRole.Admin, TeamMemberRole.User, TeamMemberRole.Guest],
        threadId
    );

    const memberCount = data?.members.filter(member => member.user.type !== TeamMemberType.Bot).length;

    return (
        <span className={subtitleClassName}>
            {strings.get("unknownUserTeamOwnerSubtitle")}
            {memberCount != null && (
                <>
                    <svg className={styles.subtitleDivider} width="12" height="16" viewBox="0 0 12 16" fill="none" aria-hidden="true">
                        <path d="M6 4.25L6 12.75" stroke="currentColor" strokeWidth="0.5" strokeLinecap="square" />
                    </svg>
                    {strings.get("unknownUserTeamMemberCountLabel")!.format(String(memberCount))}
                </>
            )}
        </span>
    );
};

/**
 * "Create a schedule" picker step for the new-user onboarding flow. Lists the
 * user's MS Teams alongside a "New team and schedule" option, letting them
 * either pick an existing team to add a schedule to, or proceed to the
 * create-new-team flow. Rendered inside the outer TeamSetupPicker modal —
 * wraps its content in FluentProvider so Fluent v9 tokens follow the Teams
 * light/dark/HC theme.
 * @param props Standard team-setup step props (close/cancel handlers, etc).
 * @returns The rendered step.
 */
const UnknownUserTeamPicker: React.FC<TeamSetupPickerStepProps> = (props) => {
    const styles = useStyles();
    const strings = StringsStore().registeredStringModules.get("teamSetupPicker")!.strings;
    const [teams, setTeams] = React.useState<microsoftTeams.TeamInformation[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [view, setView] = React.useState<View>("picker");
    const [selectedTeam, setSelectedTeam] = React.useState<microsoftTeams.TeamInformation | null>(null);
    // Whether the "New team and schedule" option is offered. Gated by the Teams admin policy that
    // can block users from creating new teams; defaults to false until the permission resolves.
    const [canCreateTeam, setCanCreateTeam] = React.useState<boolean>(false);
    // Total MS Teams the user has joined (vs. the provisionable subset rendered in the list). Kept
    // in state so both the dialog-loaded and Continue-with-new-team telemetry events can report it.
    const [totalTeamsCount, setTotalTeamsCount] = React.useState<number>(0);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [scheduleTeamsResult, joinedTeamsResult, createTeamPermissionsResult] = await Promise.allSettled([
                    TeamDataService.getTeams(),
                    TeamDataService.getMSTeamsUserJoinedTeams(),
                    UserDataService.getHasCreateTeamPermissions()
                ]);
                if (cancelled) { return; }

                // allSettled keeps the dialog usable even if one of these calls fails: unwrap each
                // fulfilled value and fall back to a safe default for any that rejected.
                const scheduleTeams = scheduleTeamsResult.status === "fulfilled" ? scheduleTeamsResult.value : [];
                const joinedTeams = joinedTeamsResult.status === "fulfilled" ? joinedTeamsResult.value.userJoinedTeams ?? [] : [];
                const hasCreateTeamPermissions = createTeamPermissionsResult.status === "fulfilled" ? createTeamPermissionsResult.value : false;

                // Only teams the user owns/administers can have a schedule provisioned, so restrict the
                // list to those. UserTeamRole.Admin covers both owners and team administrators.
                const adminTeams = joinedTeams.filter(team => team.userTeamRole === microsoftTeams.UserTeamRole.Admin);

                // Only surface MS Teams that don't already have an active schedule provisioned.
                // processMSTeamsData hands us the associated schedule team (if any) for each MS Team;
                // a team is selectable when it has no schedule or only a soft-deleted (restorable) one.
                const teamsWithoutActiveSchedule: microsoftTeams.TeamInformation[] = [];
                TeamSetupPickerUtils.processMSTeamsData(
                    scheduleTeams,
                    adminTeams,
                    (msTeamInfo, scheduleTeamInfo) => {
                        if (!scheduleTeamInfo || scheduleTeamInfo.isSoftDeleted) {
                            teamsWithoutActiveSchedule.push(msTeamInfo);
                        }
                    });
                setTeams(teamsWithoutActiveSchedule);
                setTotalTeamsCount(joinedTeams.length);
                setCanCreateTeam(hasCreateTeamPermissions);

                // Dialog-loaded telemetry: how many teams the user has in total vs. the provisionable
                // subset shown here, plus whether the create-new-team option is offered. NewTeamOptionShown
                // is always emitted (true/false) so old builds that send nothing stay distinguishable.
                InstrumentationService.logEvent(InstrumentationService.events.ConvergedOnboarding, [
                    getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.UnknownUserTeamPickerViewed),
                    getGenericEventPropertiesObject(InstrumentationService.properties.TotalNumTeams, joinedTeams.length),
                    getGenericEventPropertiesObject(InstrumentationService.properties.NumProvisionableTeams, teamsWithoutActiveSchedule.length),
                    getGenericEventPropertiesObject(InstrumentationService.properties.NewTeamOptionShown, hasCreateTeamPermissions)
                ]);
            } catch (error) {
                if (cancelled) { return; }
                const message = (error as { message?: string })?.message;
                setErrorMessage(message ?? strings.get("unknownUserLoadTeamsError") ?? null);
            } finally {
                if (!cancelled) { setLoading(false); }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleNewTeamAnScheduleFlow = () => {
        gotoNextSetupStep();
    };

    const handleScheduleIsntListedUrl = () => {
        InstrumentationService.logEvent(InstrumentationService.events.ConvergedOnboarding, [
            getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.UnknownUserTeamNotListedClicked),
            getGenericEventPropertiesObject(InstrumentationService.properties.TotalNumTeams, totalTeamsCount),
            getGenericEventPropertiesObject(InstrumentationService.properties.NumProvisionableTeams, teams.length)
        ]);
        void microsoftTeams.app.openLink(SCHEDULE_ISNT_LISTED_URL);
    };

    const provisionForTeam = async (team: microsoftTeams.TeamInformation) => {
        setView("provisioning");
        setErrorMessage(null);
        try {
            const timezone = TimeZoneUtils.getBrowserOlsonCode();
            const newTeamFields = { schedulingGroupName: strings.get("membersGroupName") };
            const createdScheduleTeamId = await TeamSetupPickerUtils.provisionScheduleTeam(team, timezone, newTeamFields);
            if (createdScheduleTeamId) {
                window.location.href = UrlFactory.getSchedulesUrl(createdScheduleTeamId);
                restartApp();
            } else {
                setErrorMessage(strings.get("unknownUserProvisionError") ?? null);
                setView("picker");
            }
        } catch (error) {
            const message = (error as { message?: string })?.message;
            InstrumentationService.trackException(error, "FailedProvisionScheduleForExistingTeam");
            setErrorMessage(message ?? strings.get("unknownUserProvisionError") ?? null);
            setView("picker");
        }
    };

    const routeToRestorableStep = (team: microsoftTeams.TeamInformation, schedules: IRestorableScheduleTeam[]) => {
        setSelectedMSTeam(team);
        setRestorableSchedules(schedules);
        // Replace the remaining onboarding steps with just the restorable picker — both
        // outcomes there (restore or create-new) navigate away, so there's no follow-up step.
        setTeamSetupPickerSequence([
            TeamSetupPickerStepState.UnknownUserTeamPicker,
            TeamSetupPickerStepState.RestorableSchedulesPicker
        ]);
        gotoNextSetupStep();
    };

    const proceedWithTeam = async (team: microsoftTeams.TeamInformation) => {
        setSelectedTeam(team);
        setErrorMessage(null);
        setView("provisioning");

        try {
            await TeamDataService.getTeams(true /* forceLoadFromNetwork */);

            const groupId = team.groupId;
            const groupEntry = groupId
                ? (UserStore().restorableSchedulesByGroup || []).find(group => group.groupId === groupId)
                : undefined;
            const schedulesForTeam = groupEntry?.teams ?? [];

            if (schedulesForTeam.length > 0) {
                routeToRestorableStep(team, schedulesForTeam);
            } else {
                await provisionForTeam(team);
            }
        } catch (error) {
            const message = (error as { message?: string })?.message;
            setErrorMessage(message ?? strings.get("unknownUserRestorableCheckError") ?? null);
            setView("picker");
        }
    };

    const handleContinue = async () => {
        if (selectedId === NEW_TEAM_SELECTION) {
            // Continue clicked with the "New team and schedule" option selected. Report the same
            // total vs. provisionable team counts as the dialog-loaded event for funnel analysis.
            InstrumentationService.logEvent(InstrumentationService.events.ConvergedOnboarding, [
                getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.UnknownUserNewTeamContinueClicked),
                getGenericEventPropertiesObject(InstrumentationService.properties.TotalNumTeams, totalTeamsCount),
                getGenericEventPropertiesObject(InstrumentationService.properties.NumProvisionableTeams, teams.length)
            ]);
            handleNewTeamAnScheduleFlow();
            return;
        }
        const team = teams.find(t => t.teamId === selectedId);
        if (team) {
            await proceedWithTeam(team);
        }
    };

    const renderHeader = (titleText: string | undefined) => (
        <div className={styles.header}>
            <span className={styles.title}>{titleText}</span>
            <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss20Regular />}
                aria-label={strings.get("unknownUserCloseAriaLabel")}
                onClick={props.onClose}
            />
        </div>
    );

    if (view === "provisioning") {
        return (
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
                <div role="region" aria-label={strings.get("unknownUserProvisioningRegionAriaLabel")} className={styles.root}>
                    <div className={styles.centerStatus}>
                        <Spinner
                            size="large"
                            label={ strings.get("unknownUserProvisioningGeneric")}
                            labelPosition="below"
                        />
                        <Text className={styles.centerStatusSubstring}>
                            {strings.get("unknownUserProvisioningSubstringForTeam")}
                        </Text>
                    </div>
                </div>
            </FluentProvider>
        );
    }

    return (
        <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
            <div role="region" aria-label={strings.get("unknownUserCreateScheduleRegionAriaLabel")} className={styles.root}>
                {renderHeader(strings.get("unknownUserCreateScheduleTitle"))}
                <p className={styles.description}>
                    {strings.get("unknownUserCreateScheduleDescription")}
                </p>
                <div className={styles.listSection}>
                    <ul className={styles.list}>
                        {/* "New team and schedule" is only offered when the Teams admin create-team policy allows it. */}
                        {canCreateTeam && (
                        <li>
                            <ToggleButton
                                appearance="subtle"
                                checked={selectedId === NEW_TEAM_SELECTION}
                                className={styles.item}
                                onClick={() => setSelectedId(NEW_TEAM_SELECTION)}
                            >
                                <div className={styles.addIconWrapper} aria-hidden="true">
                                    <Add20Filled />
                                </div>
                                <div className={styles.textContainer}>
                                    <span className={styles.itemTitle}>{strings.get("unknownUserNewTeamOptionLabel")}</span>
                                </div>
                                {selectedId === NEW_TEAM_SELECTION && (
                                    <Checkmark20Filled className={styles.checkIcon} aria-hidden="true" />
                                )}
                            </ToggleButton>
                        </li>
                        )}

                        {loading && (
                            <li className={styles.statusRow}>
                                <Spinner size="extra-small" label={strings.get("unknownUserLoadingTeamsLabel")} labelPosition="after" />
                            </li>
                        )}

                        {errorMessage && (
                            <li className={styles.errorRow}>
                                {errorMessage}
                            </li>
                        )}

                        {!loading && teams.map(team => (
                            <li key={team.teamId}>
                                <ToggleButton
                                    appearance="subtle"
                                    checked={selectedId === team.teamId}
                                    className={styles.item}
                                    onClick={() => setSelectedId(team.teamId)}
                                >
                                    <Avatar
                                        name={team.teamName}
                                        image={team.thumbnailUri ? { src: team.thumbnailUri } : undefined}
                                        shape="square"
                                        color="colorful"
                                        size={40}
                                        aria-hidden="true"
                                    />
                                    <div className={styles.textContainer}>
                                        <span className={styles.itemTitle}>{team.teamName}</span>
                                        <TeamMemberSubtitle threadId={team.teamId} subtitleClassName={styles.itemSubtitle} />
                                    </div>
                                    {selectedId === team.teamId && (
                                        <Checkmark20Filled className={styles.checkIcon} aria-hidden="true" />
                                    )}
                                </ToggleButton>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className={styles.footer}>
                    {/* Routes into the create-new-team flow, so it's hidden when the admin policy blocks
                        team creation. Empty placeholder keeps the Continue button right-aligned. */}
                    {canCreateTeam ? (
                        <Link
                            as="button"
                            className={styles.footerLink}
                            onClick={handleScheduleIsntListedUrl}
                        >
                            {strings.get("unknownUserTeamNotListedLink")}
                            <OpenRegular />
                        </Link>
                    ) : (
                        <span />
                    )}
                    <div className={styles.footerActions}>
                        <Button
                            appearance="primary"
                            disabled={!selectedId}
                            onClick={handleContinue}
                        >
                            {strings.get("unknownUserContinueButtonLabel")}
                        </Button>
                    </div>
                </div>
            </div>
        </FluentProvider>
    );
};

export default UnknownUserTeamPicker;
